<!--
  AppleSignInButton.svelte — Custom-styled "Sign in with Apple" button.

  Uses Sign in with Apple JS in popup mode (`AppleID.auth.signIn()`) rather
  than Apple's rendered button, so the control matches the rest of the auth
  page.  The popup returns an authorization code which is dispatched via the
  `credential` callback for the server to exchange.

  Apple discloses the user's name exactly once — in the response to their very
  first authorisation — so it is forwarded alongside the code.

  Retries initialisation every 500ms for up to 5 seconds to handle the async
  Apple script loading, mirroring GoogleSignInButton.

  @prop {string} [text] — 'signin_with' | 'signup_with' | 'continue_with'.
-->
<script lang="ts">
	import { onDestroy } from 'svelte';
	import { Button } from 'flowbite-svelte';
	import { configurationStore } from '../AppConfiguration';
	import { t2 } from '../i18n';

	interface Props {
		/** Text variant shown on the button. */
		text?: 'signin_with' | 'signup_with' | 'continue_with';
		/** Callback fired with the Apple authorization code and any disclosed name. */
		oncredential?: (_code: string, _name?: { firstName?: string; lastName?: string }) => void;
	}

	let { text = 'signin_with', oncredential }: Props = $props();

	/** Shape of a successful `AppleID.auth.signIn()` resolution. */
	type AppleSignInResponse = {
		authorization?: { code?: string; state?: string };
		user?: { name?: { firstName?: string; lastName?: string } };
	};

	/** The subset of the Apple JS SDK this component uses. */
	type AppleAuth = {
		init: (_config: Record<string, unknown>) => void;
		signIn: () => Promise<AppleSignInResponse>;
	};

	/** Whether the Apple SDK has been successfully initialised. */
	let appleReady = $state(false);

	/** Whether the Apple SDK failed to load after all retry attempts. */
	let appleFailed = $state(false);

	/** Retry interval handle. */
	let retryInterval: ReturnType<typeof setInterval> | null = null;

	/** Number of retry attempts made. */
	let attempts = 0;

	/** Maximum retry attempts (5 seconds at 500ms intervals). */
	const MAX_ATTEMPTS = 10;

	/** The Apple Services ID read from the server configuration. */
	let appleClientId: string | null = null;

	/** The return URL registered with Apple, sent verbatim on both legs of the flow. */
	let appleRedirectURI: string | null = null;

	/** Opaque value echoed back by Apple, used to reject responses we did not start. */
	let expectedState = '';

	/**
	 * Attempt to initialise the Apple SDK.
	 * @returns true if successful, false if the SDK is not yet available.
	 */
	function tryInitAppleID(): boolean {
		const win = window as unknown as { AppleID?: { auth: AppleAuth } };
		/** Bail out if the Apple library or configuration is not yet available. */
		if (!win.AppleID?.auth || !appleClientId || !appleRedirectURI) return false;

		expectedState = crypto.randomUUID();
		win.AppleID.auth.init({
			clientId: appleClientId,
			scope: 'name email',
			redirectURI: appleRedirectURI,
			state: expectedState,
			usePopup: true
		});

		appleReady = true;
		return true;
	}

	/**
	 * Handle the button click — open the Apple sign-in popup and forward the
	 * authorization code.  A dismissed popup rejects, which is not an error.
	 */
	async function handleClick(): Promise<void> {
		const win = window as unknown as { AppleID?: { auth: AppleAuth } };
		/** Nothing to do until the SDK is initialised. */
		if (!win.AppleID?.auth) return;

		/** Open the popup; the user may simply close it, which rejects. */
		try {
			const response = await win.AppleID.auth.signIn();
			const code = response?.authorization?.code;
			/** Ignore responses without a code or with a state we did not issue. */
			if (!code || response.authorization?.state !== expectedState) return;

			oncredential?.(code, response.user?.name);
		} catch {
			/* User closed the popup or denied access — no error to surface. */
		}
	}

	/**
	 * Start the retry loop that polls until the Apple script is ready
	 * or the maximum number of attempts is reached.
	 */
	function startRetryLoop(): void {
		if (tryInitAppleID()) return;

		/** Poll for SDK readiness at 500ms intervals until success or timeout. */
		retryInterval = setInterval(() => {
			attempts++;
			if (tryInitAppleID() || attempts >= MAX_ATTEMPTS) {
				if (!appleReady) appleFailed = true;
				if (retryInterval) clearInterval(retryInterval);
				retryInterval = null;
			}
		}, 500);
	}

	/**
	 * React to configuration store changes.
	 * Once the config arrives, extract the Apple settings and begin
	 * initialisation.  If they are missing, mark as failed.
	 */
	const unsubscribe = configurationStore.subscribe((config) => {
		if (!config) return;

		appleClientId = config.appleClientId;
		appleRedirectURI = config.appleRedirectURI;

		/** Abort if Apple sign-in is not configured on the server. */
		if (!appleClientId || !appleRedirectURI) {
			appleFailed = true;
			return;
		}

		/* Only start if we haven't already initialised. */
		if (!appleReady && !retryInterval) {
			startRetryLoop();
		}
	});

	onDestroy(() => {
		unsubscribe();
		if (retryInterval) clearInterval(retryInterval);
	});

	/** Human-readable label based on the text prop. */
	const labelMap: Record<string, string> = {
		signin_with: t2('Sign in with Apple'),
		signup_with: t2('Sign up with Apple'),
		continue_with: t2('Continue with Apple')
	};
</script>

{#if $configurationStore?.appleClientId || !$configurationStore}
	<Button color="alternative" class="w-full" disabled={!appleReady || appleFailed} onclick={handleClick}>
		<svg
			class="mr-2"
			width="18"
			height="18"
			viewBox="0 0 384 512"
			fill="currentColor"
			xmlns="http://www.w3.org/2000/svg"
			aria-hidden="true"
		>
			<path
				d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"
			/>
		</svg>
		{labelMap[text] || t2('Sign in with Apple')}
	</Button>
{/if}
